import { createContext, useContext, useEffect, useMemo, ReactNode } from "react";
import { BrandConfig, resolveBrand } from "@/config/brand";
import { loadClarity } from "@/lib/analytics-loader";

/* ─── Asset imports (so Vite hashes them properly) ─── */
import etbLogoTag from "@/assets/etb-logo-tag.svg";
import etbLogoTagWhite from "@/assets/etb-logo-tag-white.webp";
import etbIcon from "@/assets/etb-icon.webp";
import etbLogoPng from "@/assets/etb-logo.png";
import flowLogoFullBlue from "@/assets/flow-logo-full-blue.png";
import flowLogoSidebar from "/icons/favicon-96x96.png";

/** Resolved logo URLs (Vite-hashed) mapped by brand id */
const resolvedLogos: Record<string, BrandConfig["logos"]> = {
  etb: { navbarDark: etbLogoTagWhite, icon: etbLogoTag, login: etbLogoTag, sidebar: etbLogoTag },
  flow: { navbarDark: flowLogoFullBlue, icon: flowLogoSidebar, login: flowLogoFullBlue, sidebar: flowLogoFullBlue },
};

const BrandContext = createContext<BrandConfig>(resolveBrand());

export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }: { children: ReactNode }) {
  const brand = useMemo(() => {
    const b = resolveBrand();
    // Override logo paths with Vite-resolved imports
    return { ...b, logos: resolvedLogos[b.id] ?? b.logos };
  }, []);

  // Inject CSS variable overrides. The dark class is applied by next-themes
  // AFTER this effect can run (and toggles at runtime), so we re-apply the
  // brand vars whenever <html>'s class changes instead of reading it once.
  useEffect(() => {
    const root = document.documentElement;
    const varKeys = new Set([...Object.keys(brand.cssVars), ...Object.keys(brand.cssVarsDark)]);

    const applyBrandVars = () => {
      const isDark = root.classList.contains("dark");
      const vars = isDark ? { ...brand.cssVars, ...brand.cssVarsDark } : brand.cssVars;
      // Clear first: a dark-only key (e.g. primary-foreground) must not
      // linger when switching back to light.
      varKeys.forEach((k) => root.style.removeProperty(`--${k}`));
      Object.entries(vars).forEach(([k, v]) => {
        root.style.setProperty(`--${k}`, v);
      });
    };

    applyBrandVars();
    const observer = new MutationObserver(applyBrandVars);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    // Update document title
    document.title = `${brand.name} — Plataforma Omnicanal`;

    // Update all favicons (rel="icon" and rel="apple-touch-icon")
    const favicons = document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]');
    favicons.forEach((link) => {
      link.href = brand.favicon;
    });

    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", `Centraliza WhatsApp, Instagram, Facebook Messenger y más en un solo inbox. Plataforma omnicanal potenciada por IA de ${brand.shortName}.`);
    }

    // Update OG meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", `${brand.name} — Plataforma Omnicanal`);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute("content", `${brand.name} — Plataforma Omnicanal`);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", `Centraliza WhatsApp, Instagram, Facebook Messenger y más en un solo inbox. Plataforma omnicanal potenciada por IA de ${brand.shortName}.`);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute("content", `Centraliza WhatsApp, Instagram, Facebook Messenger y más en un solo inbox. Plataforma omnicanal potenciada por IA de ${brand.shortName}.`);

    loadClarity(brand.clarityId);

    return () => {
      observer.disconnect();
      varKeys.forEach((k) => {
        root.style.removeProperty(`--${k}`);
      });
    };
  }, [brand]);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
}
